---
title: "Don't burn out on me"
subtitle: "on resignation and surrender"
date: 2025-11-04 23:12
---

import { GraphContainer, Section, SectionLink } from './Graph';
import { Variant } from '#components/Variant';

<Variant openInternalLinksInNewTab hideFootnoteLabels>
<GraphContainer>

<Section id={0}>

In [ALIGNMENT,](https://mindslice.substack.com/p/alignment) [Vincent](https://www.vvhuang.com/) writes:

> “are you worried at all about burnout?” i asked
>
> “nobody ever gets burned out from working too hard,” you declared. “burnout comes from working towards a cause you no longer believe in, or from otherwise doing something you don’t really want to do”

This reminds me of something a former manager once told me. Burnout needs two factors: The first is <SectionLink id={0}>putting a lot of work in.</SectionLink> The second is not <SectionLink id={8}>getting a lot out.</SectionLink> <SectionLink id={4}>Large costs spent for small gains.</SectionLink>

</Section>
<Section id={1}>

What are the costs I've paid? Or, in a more dismal framing: what has my job taken away from me?

The obvious cost is time. Work takes out fifty-ish hours a week, inclusive of commuting. On the scale of things, that's not too bad of a cost to pay. I've heard friends complain about eighty-hour weeks, and I've never had to work that much.

Then there's the mental toll that work takes. Many days, I'd come home from work, and I'd be too exhausted to write, or code for myself, or work on other kinds of personal projects. But [this is nothing new.](/post/easy-hard-things/) I've complained about this since my first job.

The cost that feels the most painful, though, the one that feels like I'm paying for in fractions of my soul, the one that's grown so much in the last two months, is the emotional cost:

* the weakness I feel from working on a project that I don't like, but *choosing* to work on it anyway, because I think it'll help me stay here;

* the exhaustion I feel after my patience has run out, again and again, after people have told me to wait for change;

* the anger I feel when the best thing for me to work on, the thing that'll be best for the company, is *not* what I believe will be best for our users.

How do you pay all these costs and <SectionLink id={1}>keep going?</SectionLink> What am I even <SectionLink id={13}>getting out of all this?</SectionLink>

</Section>
<Section id={2}>

My second favorite Imagine Dragons song, after Demons, is [Burn Out,](https://genius.com/Imagine-dragons-burn-out-lyrics) and it asks the same question: how do you keep going when the costs go up? It starts with:

> Patience only gets you so far  
> Blood will get you further  
> Pain will only make your heart hard  
> Tossed in fury's weather

In the first two lines, the singer tells us that they've tried being patient. They've realized that, to get what they want, they need to do more than wait for things to happen. They've realized that *blood* will keep them going when their patience runs out; I take blood as a metaphor for self-sacrifice.

We then hear the results of their sacrifice. It leads to additional *pain*, pain on top of the hurt they already feel from *fury's weather*. The singer worries that this pain would lead to burnout, would lead to making their *heart hard*. The verse continues:

> Innocence is beautiful to see  
> Won't you box it up for me?  
> For me

They plea, *lend me your innocence.* Wrapped in this request is a question: *how do you remain so innocent?* How do you keep going when you're in pain? When you have to spill your own blood to keep going, how do you <SectionLink id={2}>not bleed out?</SectionLink> Dear gods, is going through fury's weather <SectionLink id={18}>even possible?</SectionLink>

</Section>
<Section id={3}>

Is it possible to spend lots of effort without bleeding out? Can exerting yourself *ever* be worth it?

Say you hold yourself back, and do the bare minimum to skate by. Either you get the minimum reward, or you get lucky and get more. If you're already rewarded elsewhere---if the rest of your life is fulfilling already---why bother trying? You work the nine-to-five, you collect your salary, you meet expectations, and never exceed them. You rest and vest. You coast.

Maybe you're guaranteed a bigger reward, if you put in more effort. Maybe you're playing a video game you *know* you could get better at if you practiced more. Maybe you're preparing for a test you *know* you can get a better grade for if you studied harder. Then *that's* the reason to grind, when you know your efforts won't be wasted.

But what if the reward is unpredictable? You don't know, when you're in the middle of a hit-or-miss project, whether it'll flop. Your success or failure might be dictated by market forces, or the phase of the moon. Then why waste your energy and risk burning out? What reason do you have to <SectionLink id={3}>put in more effort?</SectionLink>

</Section>
<Section id={4}>

If burnout is what you get when you put a lot of work in, and not get a lot out, then what about the other possibilities? Consider the matrix of effort against reward:

|              | Small reward | Large reward |
| -            | -            | -            |
| Small effort | coasting     | thriving     |
| Large effort | burning out  | grinding     |

For our discussion, let's assume the difference between *small reward* and *large reward* isn't a matter of need. The *large reward* must be something nice-to-have, but not necessary.

You can put a lot in and get a lot out. I've called this *grinding*. I intend two connotations: first, one of hustle and dogged pursuit; and second, one of delicate balance, with the potential of burnout when returns diminish.

You can put a little in and get a little out. This is *coasting*. Maybe you're wasting your potential or whatever, but at least you're not burning out. Nothing ventured, nothing lost. You're giving yourself [a speed limit.](/post/speed-limit/)

And last, there's the quadrant we all want to be in. The one where you put a little in, and get a lot more out. I've struggled to name this quadrant, because your reward can come from any number of reasons. Maybe it's leverage or fit; maybe it's luck or circumstance. *Thriving* is a good-enough name.

Often, we don't control <SectionLink id={5}>what rewards we get.</SectionLink> In these situations, why bother <SectionLink id={14}>spending more effort?</SectionLink>

</Section>
<Section id={5}>

To be clear: I get plenty of rewards from work. I make a salary large enough that I've never had to worry about finances. I love hanging out with my coworkers. But in many other respects, my job feels lacking.

My schedule isn't as flexible as I want it to be. We tell ourselves that the only thing that matters is our output, the area under the curve, and that it's [okay if some days are unproductive,](https://mitadmissions.org/blogs/entry/there-is-no-such-thing-as-a-typical-day/) because we are only judged by our results. Then why do I have daily standups? Why am I expected the same number of hours at work each day? Neither of these are *mandated*, but these are the norm, at least.

In all the ways a project can be engaging, my current one isn't. I find toying with LLMs to be insipid, past the day's first hour or two. My project has the potential to be great, but I'm not sure if that matters when *I don't know what I'm doing*. Negative results discourage me, and that's when I get results in the first place---the feedback loops take so long, because everything AI-related is slow.

You could take it as a challenge. Find *something* here that's interesting, CJ! You'll feel better after you <SectionLink id={19}>ship *something*!</SectionLink> Pour enough chocolate on your broccoli and it'll work out!

I don't even know if that's possible. I don't even know what will <SectionLink id={6}>make me satisfied.</SectionLink> I don't even know if <SectionLink id={15}>there's *any* job</SectionLink> that'll make me feel happy.

</Section>
<Section id={6}>

Because maybe it's not about the storm I'm going through, maybe it's not about the project I'm working on, maybe I'll *always* be dissatisfied. After all, I've been complaining since forever.

From [Infinity:](https://cmsw.mit.edu/angles/2020/infinity-quines/)

> Sure I made the PMO, but not the IMO. I made friends, but couldn't get close to them. I discovered myself, at the risk of getting thrown out of home. It felt as if every time I climbed a mountain, there'd be a higher peak taunting me. As if I was counting higher and higher, but never reaching infinity.

From [Clouds over Rochester:](/post/clouds-over-rochester/)

> Maybe I want someone to tell me *no*, what I'm looking for is impossible, it's impossible for *me* specifically, because I'm special, because I'm destined to have that little nagging suffering forever. If I believed that, I'd still suffer. But at least I'd suffer in peace.

From [etymologies:](/post/etymologies/)

> When I talked about my career goals, I said I only wanted to do enough to not get terminated. To do enough, to comply, to discharge responsibility; these are the etymologies of *satisfy*. What I want is to satisfy my employer, and somehow this would satisfy me. But why would it satisfy me? Why would settling be enough?

Wouldn't it be easier---so much easier!---if I simply resigned myself? If I simply believed that I'd <SectionLink id={7}>never be truly happy?</SectionLink>

</Section>
<Section id={7}>

What do I want to get out from work? I've been asking myself this question for [five years now.](https://mitadmissions.org/blogs/entry/soulsearching/#four) Around two years ago, I made a list. I've reordered it a bit, but it's mostly the same. From most to least important, I want a job that lets me:

- Have financial security
- Make friends
- Have a flexible schedule
- Do things I enjoy
- Do many different things
- Mentor others
- Do important things
- Decide what I work on
- Be visible
- Build wealth
- Do things I'm good at
- Take ownership over things
- Challenge myself
- Do interesting things
- Learn a lot
- Support loved ones
- Travel the world
- Take care of my health

Note that I don't care about *everything* in this list; for example, I don't *really* want to travel the world. I think I stole this from a similar list, from somewhere I didn't record and can't remember.

While the list feels right in the *relative* sense, it doesn't capture the *absolute* difference between the items. The gap between "do important things" and "decide what I work on" is quite small, while the gap between "do interesting things" and "learn a lot" is quite big.

I came up with this list while I was at my last job, thinking about what I wanted from the next one. I'd hoped that my current job would give me these things, so the question is <SectionLink id={16}>does it?</SectionLink> Perhaps more importantly: are these things that I <SectionLink id={9}>should be getting,</SectionLink> whatever *should* means?

</Section>
<Section id={8}>

What I *think* I want from a job, differs from what the [job satisfaction research](https://80000hours.org/articles/job-satisfaction-research/) says. From most to least important, I *should* want a job that lets me:

- Engage with work
  - Challenge myself
  - Take ownership over things
  - Decide what I work on
  - Do many different things
  - Do interesting things
- Help others
  - Do important things
  - Mentor others
- Do things I'm good at
- Make friends
- Meet my basic needs
  - Take care of my health
  - Have financial security
  - Have a flexible schedule
  - Support loved ones

And that my remaining factors are poor predictions of job satisfaction:

- Do things I enjoy
- Be visible
- Build wealth
- Learn a lot
- Travel the world

What strikes me is how important *engaging work* is, compared to where I'd put it. Engaging work is nice, but I didn't think it was *that* important. I've always thought I could be happy doing something uninteresting, above a some baseline of boringness. Or rather: I've always thought that challenging and interesting work wouldn't make me *happier* with my job.

The thing is, though, I've never had a day job that lets me do engaging work long-term. I don't know whether such a job exists, let alone what kinds of things I'd find engaging. I don't even know whether this research <SectionLink id={10}>applies to *me*.</SectionLink>

</Section>
<Section id={9}>

Accomplishing things, finishing things, having ownership over things, whatever. Studies say these things should feel good. Task identity is one of the components of [job characteristic theory.](https://en.wikipedia.org/wiki/Job_characteristic_theory#Core_job_characteristics) At work, we celebrate when people ship things, and people sure seem happy when they do. And job-seekers claim they want to be involved in the entire process of a project, because that's a part of ownership.

I don't think I've experienced that. From [it's been nine years and I still want out:](/post/still-want-out)

> when i do accomplish things, there isn't space to pause and celebrate and squeeze whatever hypothetical fulfillment out. there's always another mountain to set my sights on, another test to pass. maybe it'd be better if i did feel aspiration or fulfillment. but i wonder if i'm only here in the first place, if i've done as much as i have, is because i don't.

A finished project doesn't feel like a reward, it feels like a *burden*. A permanent addition to my maintenance costs. More weight on my shoulders to worry about. And I don't *typically* mind these things. After all, I've often [enjoyed maintenance](https://mitadmissions.org/blogs/entry/you-dont-have-to-be-a-founder/) more than creation.

Yet the industry always asks for more. The market forces ignore quality. The backlog becomes a black hole. I've been asked to deprioritize customer-reported issues, so I could focus on my project.

I've been complaining about how unengaging work is, but I don't think engaging work would feel any more satisfying. That's part of why I can't imagine what a <SectionLink id={11}>better, *feasible* job</SectionLink> might look like. Because if I'll be dissatisfied either way, <SectionLink id={17}>maybe it's not about the job.</SectionLink>

</Section>
<Section id={10}>

I can imagine what jobs I'd be happy in, sure. But I don't know what a better, *feasible* job would be.

In [soulsearching / naubos na,](https://mitadmissions.org/blogs/entry/soulsearching/#three) I talked about being a high school teacher, or being an individual tutor. In [day jobs,](/post/day-jobs/) I talked about running logistics at a non-profit, or working on graduate-level math textbooks. But I also mention, in these same posts, that none of these things are practical. They're not careers I can explore, not if I want to stay in the US, not under my current visa status.

Then again, it's not like work has been *bad*. Sure, I didn't [*love* my last job,](/post/the-day-to-day/) but I didn't hate it either. And while I've complained about Stainless, I do *like* working here, even if I don't *love* it.

Maybe I'm just asking too much, then. Plenty of people don't love their jobs, and *they* go along in life just fine. Why makes *me* so special, then, for me to demand more? Why complain, when it's already a huge privilege to even have a job that lets me stay in the US? Why can't I just settle for <SectionLink id={12}>a job that's *okay*?</SectionLink>

</Section>
<Section id={11}>

The pre-chorus to Burn Out goes:

> Oh, give me strength, and give me peace  
> Does anyone out there want to hear me?

The singer seems to address this to some godlike figure. Supplication, then. A prayer asking for something, in this case, *strength*, or *peace*.

I'm an atheist. I don't believe in gods. But I find it convenient to address my words to god, sometimes, in the hopes that she's listening to my prayers. From [i like me better depressed:](/post/better-depressed)

> i want god to take away my desire to work on stuff, if she isn't going to give me the willpower to work on them. i want god to take away my guilt for not working on stuff, if she isn't going to take away my desire to work on stuff. i want god to make me sleep and not wake up, if she isn't going to take away my guilt for not doing stuff.

When I was still a Christian, I knew that God didn't grant every request. Sometimes a prayer gets denied because it's not according to plan, and we ask, ["not my will, but yours be done."](https://www.biblegateway.com/passage/?search=Luke%2022%3A42&version=NIV)

Suppose I pray for my chronic illness to get better, and years and years pass, and it doesn't. Should I stop praying? Should I give up, and resign myself to being sick? Should I stop hoping for a world where I get better? And the answer I've been told is:

* *no*, that I keep praying, because prayer isn't about getting God to do what you want, it's about expressing faith, and devotion;

* *no*, that it's not about accepting that God wants me to be sick, it's accepting what God wants, *whatever it may be*;

* *no*, that I can still have desires, and express them, and engage with them, but I must detach myself from the outcomes;

* that I'm not *giving up* on putting more effort in, but *letting go* of hoping for a specific reward;

* that I'm not *settling* on a flawed situation, but *accepting* the one that I have;

* that I'm not *resigning* myself to permanent dissatifaction, but *surrending* to the what-is, and the what-will-be;

and so, the pre-chorus leads to the chorus---

> It's just another downpour, don't let it get the best of you  
> It's only up from the floor, light everything inside of you  
> Don't burn out, don't burn out on me  
> Don't burn out, don't burn out on me

---a response to their prayer, maybe, but a non-response to their questions. Neither strength, nor peace, but an exhortation. No advice for what to do, only a plea for what not to do. Maybe that's enough. It'll have to be enough.

</Section>

</GraphContainer>
</Variant>
